import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Button,
  CollectionPreferences,
  FormField,
  Header,
  Pagination,
  Select,
  SpaceBetween,
  Table,
  TextFilter,
} from "@amzn/awsui-components-console";
import { OptionDefinition } from "node_modules/@amzn/awsui-components-console/internal/components/option/interfaces";
import React, { useEffect, useMemo, useState } from "react";

import { TablePreferences } from "@/components/chat/types";
import useAdminChatHistory from "@/hooks/admin/useAdminChatHistory";
import useAdminGetUsers from "@/hooks/admin/useAdminGetUsers";
import useReadOnlyChatModal from "@/hooks/admin/useReadOnlyChatModal";
import { ChatHistoryItem } from "@/types/chat-history";
import {
  ADMIN_DEFAULT_PAGE_SIZE,
  adminPageSizePreference,
} from "@/utils/adminTable";
import { ULID_COLUMN_WIDTH_PX } from "@/utils/identifiers";
import { formatTimestamp, formatUnits, truncateText } from "@/utils/utils";

import EmptyState from "../EmptyState";

/**
 * Width of the Title column, in pixels. Narrower than the ID column beside it
 * (274px for a ULID) because a truncated title is still recognisable, whereas a
 * clipped id is neither copyable nor comparable. Titles longer than this clip
 * with an ellipsis and stay readable via the cell's tooltip.
 */
const TITLE_COLUMN_WIDTH_PX = 240;

const ChatHistory: React.FC = () => {
  const { loading: usersLoading, items: users } = useAdminGetUsers();
  const {
    loading: historyLoading,
    items: historyItems,
    loadAdminChatHistory,
  } = useAdminChatHistory();
  const [selectedUser, setSelectedUser] = useState<OptionDefinition | null>(
    null
  );
  // State, not a ref: the page size is rendered — it decides how many rows the
  // table shows — so changing it has to re-render.
  const [preferences, setPreferences] = useState<TablePreferences>({
    pageSize: ADMIN_DEFAULT_PAGE_SIZE,
  });

  const userItems = useMemo<OptionDefinition[]>(
    () =>
      users
        ?.sort((a, b) => a.name.localeCompare(b.name))
        .map(({ userId, name }) => ({
          label: name,
          value: userId,
        })),
    [users]
  );

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(historyItems, {
    filtering: {
      empty: <EmptyState title="No items available" />,
      noMatch: (
        <EmptyState
          title="No matches"
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },
    pagination: {
      // Show a fuller page of a user's chat history by default; admins were
      // paging through 10-row chunks to review activity.
      pageSize: preferences.pageSize,
    },
    // Ordered by Updated At, most recent first: an admin opening a user's
    // history is normally looking at recent activity, and the newest
    // conversations are what page 1 should hold. Sorting stays client-side —
    // the hook hands over the user's whole history, so this orders the full set
    // rather than just the page on screen. Title remains sortable by clicking
    // its header, for finding a conversation by the name one remembers.
    sorting: {
      defaultState: {
        sortingColumn: { sortingField: "lastUpdated" },
        isDescending: true,
      },
    },
  });

  const { toggle: toggleChatModal, ModalComponent: ReadOnlyChatModal } =
    useReadOnlyChatModal({
      user: selectedUser
        ? users.find(({ userId }) => userId === selectedUser.value)
        : undefined,
    });

  useEffect(() => {
    if (selectedUser?.value) {
      loadAdminChatHistory(selectedUser.value);
    }
  }, [selectedUser, loadAdminChatHistory]);

  return (
    <SpaceBetween size="m">
      <FormField label="User">
        <Select
          selectedOption={selectedUser}
          onChange={({ detail }) => setSelectedUser(detail.selectedOption)}
          options={userItems}
          {...(usersLoading
            ? {
                loadingText: "Loading users",
                statusType: "loading",
              }
            : {})}
          disabled={!usersLoading && userItems.length <= 0}
          placeholder={
            !usersLoading && userItems.length <= 0
              ? "No users available"
              : "Choose a user"
          }
        />
      </FormField>
      {selectedUser && (
        <Table
          {...collectionProps}
          header={
            <Header
              counter={`(${historyLoading ? "..." : historyItems.length})`}
            >{`Chat History for ${
              selectedUser.label ?? truncateText(selectedUser.value!, 10)
            }`}</Header>
          }
          items={items}
          // Row identity follows the conversation id, not the row's position:
          // the table now reorders by default, and a position-keyed row would
          // carry its previous conversation's state to whatever lands there.
          trackBy="id"
          loading={historyLoading}
          renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
            `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
          }
          columnDefinitions={[
            {
              id: "id",
              header: "ID",
              // Wide enough for a whole ULID, and `minWidth` so a resize or a
              // flex squeeze cannot take it below that: a clipped id can be
              // neither copied nor compared.
              width: ULID_COLUMN_WIDTH_PX,
              minWidth: ULID_COLUMN_WIDTH_PX,
              cell: ({ id }: ChatHistoryItem) => (
                // `nowrap` is what keeps the id on one unbroken line. overflow
                // + textOverflow are the fallback for content wider than the
                // column: nowrap alone would let it spill past the boundary,
                // the two together clip it with an ellipsis instead.
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <Button
                    variant="inline-link"
                    onClick={() => toggleChatModal(id)}
                  >
                    {id}
                  </Button>
                </span>
              ),
              sortingField: "id",
            },
            {
              id: "title",
              header: "Title",
              // Titles are free text and were letting this column grow to fit
              // the longest one. `width` on its own would not shrink it: this
              // table sets no `wrapLines`, so a title cannot break across lines
              // and the column's minimum content width is that longest title.
              // Clipping the cell is what lets the column narrow; `maxWidth`
              // then caps it (honoured because the table is not resizable).
              width: TITLE_COLUMN_WIDTH_PX,
              maxWidth: TITLE_COLUMN_WIDTH_PX,
              cell: ({ title }: ChatHistoryItem) => (
                // Same idiom as the ID column above. `display: block` is needed
                // for the ellipsis: an inline span does not clip its overflow.
                // The native tooltip keeps the full title readable on hover,
                // since it is now the truncated value on screen.
                <span
                  title={title}
                  style={{
                    display: "block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {title}
                </span>
              ),
              sortingField: "title",
              isRowHeader: true,
            },
            {
              id: "created",
              header: "Created On",
              cell: ({ created }: ChatHistoryItem) => formatTimestamp(created),
              sortingField: "created",
              isRowHeader: true,
            },
            {
              id: "lastUpdated",
              header: "Updated At",
              cell: ({ lastUpdated }: ChatHistoryItem) =>
                formatTimestamp(lastUpdated),
              sortingField: "lastUpdated",
              isRowHeader: true,
            },
            {
              id: "isPinned",
              header: "Pinned",
              cell: ({ isPinned }: ChatHistoryItem) =>
                isPinned ? "Yes" : "No",
              sortingField: "isPinned",
              isRowHeader: true,
            },
            {
              id: "isArchived",
              header: "Archived",
              cell: ({ isArchived }: ChatHistoryItem) =>
                isArchived ? "Yes" : "No",
              sortingField: "isArchived",
              isRowHeader: true,
            },
          ]}
          pagination={<Pagination {...paginationProps} />}
          filter={
            <TextFilter
              {...filterProps}
              countText={`${filteredItemsCount} ${formatUnits(
                filteredItemsCount!,
                "item"
              )}`}
              filteringAriaLabel="Filter items"
            />
          }
          preferences={
            <CollectionPreferences
              title="Preferences"
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={preferences}
              pageSizePreference={adminPageSizePreference()}
              onConfirm={({ detail }) =>
                setPreferences({
                  pageSize: detail.pageSize ?? ADMIN_DEFAULT_PAGE_SIZE,
                })
              }
            />
          }
        />
      )}
      {ReadOnlyChatModal()}
    </SpaceBetween>
  );
};

export default ChatHistory;
